import { useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import type { TaskFeedPlatform } from '@/app/[locale]/tasks/components/TaskFeedCard/taskFeedCard.constants'
import {
  collectFeedPlatforms,
  toFeedPlatformsParam
} from '@/app/[locale]/tasks/components/TasksFeedNav/feedPlatforms'
import {
  type TasksFeedSortId,
  toFeedSortParam
} from '@/app/[locale]/tasks/components/TasksFeedNav/tasksFeedNav.constants'
import type { TasksFilterSegmentId } from '@/app/[locale]/tasks/components/TasksFilterBar/tasksFilterBar.constants'
import type { TaskCardDemoRow } from '@/app/[locale]/tasks/components/TasksTaskCards/tasksTaskCards.constants'

import { getTaskGroups, taskGroupsAsAvailablePage } from '../api/taskGroupsApi'
import type {
  AvailableTaskCard,
  AvailableTasksStatus,
  ListAvailableTasksResponse,
  TaskFeedFilterParams,
  TaskListTypeFilter
} from '../api/watchTaskApi'
import { mapAvailableTaskCardToTaskRow } from '../mappers/mapAvailableTaskCardToTaskRow'
import {
  TASKS_AVAILABLE_PAGE_SIZE,
  TASKS_FAN_LIST_FIRST_PAGE
} from '../tasks.constants'
import { useFanTasksAccess } from './useFanTasksAccess'
import { useStickyFeedPlatforms } from './useStickyFeedPlatforms'

function segmentToApiType(
  segment: TasksFilterSegmentId
): TaskListTypeFilter | undefined {
  return segment === 'all' ? undefined : segment
}

function dedupeAvailableTasksById(
  tasks: AvailableTaskCard[]
): AvailableTaskCard[] {
  const seen = new Set<string>()
  const unique: AvailableTaskCard[] = []

  for (const task of tasks) {
    const id = task.task_id
    if (!id || seen.has(id)) {
      continue
    }
    seen.add(id)
    unique.push(task)
  }

  return unique
}

/** Type, platform and sort are applied by the API: pages are shown as they come. */
function mapPagesToRows(
  pages: { tasks: AvailableTaskCard[] }[] | undefined
): TaskCardDemoRow[] {
  if (!pages?.length) {
    return []
  }
  const tasks = dedupeAvailableTasksById(pages.flatMap(page => page.tasks))
  return tasks.map(task => mapAvailableTaskCardToTaskRow(task))
}

/** The freshest page's platforms facet; it covers every page of the list. */
function lastPagePlatforms(
  pages: ListAvailableTasksResponse[] | undefined
): string[] | undefined {
  return pages?.at(-1)?.available_platforms
}

function getNextAvailablePage(
  lastPage: { tasks: AvailableTaskCard[]; total: number },
  allPages: { tasks: AvailableTaskCard[]; total: number }[],
  lastPageParam: number
): number | undefined {
  const chunk = lastPage.tasks.length
  if (chunk === 0 || chunk < TASKS_AVAILABLE_PAGE_SIZE) {
    return undefined
  }
  const loaded = allPages.reduce((n, p) => n + p.tasks.length, 0)
  if (loaded >= lastPage.total) {
    return undefined
  }
  return lastPageParam + 1
}

/** One page of Explore (a platform on offer for a retry is listed too) or of Soon. */
async function fetchAvailablePage(
  page: number,
  pageSize: number,
  type: TaskListTypeFilter | undefined,
  filter: TaskFeedFilterParams & { status?: AvailableTasksStatus }
): Promise<ListAvailableTasksResponse> {
  return taskGroupsAsAvailablePage(
    await getTaskGroups('available', {
      page,
      page_size: pageSize,
      type,
      ...filter
    })
  )
}

export function useAvailableTasksInfinite(
  sort: TasksFeedSortId,
  filterSegment: TasksFilterSegmentId,
  pickedPlatforms: readonly TaskFeedPlatform[] = []
) {
  const { tasksListAuthScope } = useFanTasksAccess()
  const typeFilter = segmentToApiType(filterSegment)
  const feedFilter = {
    platforms: toFeedPlatformsParam(pickedPlatforms),
    sort: toFeedSortParam(sort)
  }

  const sharedQueryOptions = {
    initialPageParam: TASKS_FAN_LIST_FIRST_PAGE,
    getNextPageParam: getNextAvailablePage,
    staleTime: 30_000,
    // Do not remount-refetch after a failed first load — that remounted the
    // list UI and re-triggered the query in a tight loop (OCE-2538 follow-up).
    refetchOnMount: false as const,
    refetchOnReconnect: false as const,
    retry: false as const
  }

  const {
    data: availableData,
    isFetched: availableIsFetched,
    isError: availableIsError,
    hasNextPage: availableHasNextPage,
    isFetchingNextPage: availableIsFetchingNextPage,
    fetchNextPage: fetchAvailableNextPage,
    refetch: refetchAvailable
  } = useInfiniteQuery({
    queryKey: [
      'tasks',
      'available',
      'start',
      typeFilter,
      feedFilter,
      tasksListAuthScope
    ],
    queryFn: ({ pageParam }) =>
      fetchAvailablePage(
        pageParam,
        TASKS_AVAILABLE_PAGE_SIZE,
        typeFilter,
        feedFilter
      ),
    ...sharedQueryOptions
  })

  const {
    data: soonData,
    isFetched: soonIsFetched,
    refetch: refetchSoon
  } = useInfiniteQuery({
    queryKey: [
      'tasks',
      'available',
      'soon',
      typeFilter,
      feedFilter,
      tasksListAuthScope
    ],
    queryFn: ({ pageParam }) =>
      fetchAvailablePage(pageParam, TASKS_AVAILABLE_PAGE_SIZE, typeFilter, {
        ...feedFilter,
        status: 'SOON'
      }),
    ...sharedQueryOptions
  })

  const availableRows = useMemo(
    () => mapPagesToRows(availableData?.pages),
    [availableData]
  )

  const comingSoonRows = useMemo(
    () => mapPagesToRows(soonData?.pages),
    [soonData]
  )

  // The Platforms menu: every platform of both lists (platform filter
  // aside), known once each list answered; a failed list offers none.
  const platformsFetched = availableIsFetched && soonIsFetched
  const fetchedPlatforms = useMemo(
    () =>
      platformsFetched
        ? collectFeedPlatforms([
            lastPagePlatforms(availableData?.pages),
            lastPagePlatforms(soonData?.pages)
          ])
        : undefined,
    [availableData, platformsFetched, soonData]
  )
  const platforms = useStickyFeedPlatforms(fetchedPlatforms)

  const query = useMemo(
    () => ({
      // A Soon failure must not block Explore: an empty Soon is a valid
      // catalog state.
      isFetched: availableIsFetched && soonIsFetched,
      isError: !availableData?.pages.length && availableIsError,
      hasNextPage: availableHasNextPage,
      refetch: async () => {
        await Promise.all([refetchAvailable(), refetchSoon()])
      }
    }),
    [
      availableData,
      availableHasNextPage,
      availableIsError,
      availableIsFetched,
      refetchAvailable,
      refetchSoon,
      soonIsFetched
    ]
  )

  const handleLoadMore = useCallback(() => {
    if (availableHasNextPage && !availableIsFetchingNextPage) {
      void fetchAvailableNextPage()
    }
  }, [
    availableHasNextPage,
    availableIsFetchingNextPage,
    fetchAvailableNextPage
  ])

  const countQuery = useCallback(
    (picked: readonly string[]) => ({
      queryKey: [
        'tasks',
        'available',
        'count',
        typeFilter,
        picked,
        tasksListAuthScope
      ] as const,
      queryFn: async () =>
        (
          await getTaskGroups('available', {
            page: TASKS_FAN_LIST_FIRST_PAGE,
            page_size: 1,
            type: typeFilter,
            platforms: picked.length > 0 ? picked : undefined
          })
        ).total
    }),
    [tasksListAuthScope, typeFilter]
  )

  return {
    query,
    /** The phone sheet's «Show N tasks» for picked platforms. */
    countQuery,
    platforms,
    platformsFetched,
    availableRows,
    comingSoonRows,
    handleLoadMore,
    isFetchingNextPage: availableIsFetchingNextPage
  }
}
